import type { ThemePreference } from "../../shared/mutators/setUserPreference";
import { Favorites } from "./Favorites";
import { Recents } from "./Recents";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { config } from "../environment";
import { useChordShortcuts } from "../hooks/useChordShortcuts";
import { useColorMode } from "../hooks/useColorMode";
import { useCommandBar } from "../hooks/useCommandBar";
import { useIntercomMessenger } from "../hooks/useIntercomMessenger";
import { useKonamiCode } from "../hooks/useKonamiCode";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { browserNotificationSettings } from "../lib/browserNotifications";
import { soundSettings } from "../lib/sounds";
import { navigate, Route, routes, Switch } from "../lib/urls";
import { setSealingKey } from "../sealSecret";
import { Connectors } from "./Connectors";
import { CommandBar } from "./CommandBar";
import { Credits } from "./Credits";
import { GalleryImportRoute } from "./GalleryImportRoute";
import { GlobalModals } from "./GlobalModals";
import { IntercomMessenger } from "./IntercomMessenger";
import { Links } from "./Links";
import { KeyboardShortcuts } from "./KeyboardShortcuts";
import { NewUserIntroVideo } from "./NewUserIntroVideo";
import { PersonalChats } from "./PersonalChats";
import { WorkflowChatEntry } from "./PersonalChats/WorkflowChatEntry";
import { Settings } from "./Settings";
import { Monitoring } from "./Monitoring";
import { MobileNavigationProvider } from "./MobileNavigation";
import { Skills } from "./Skills";
import { Space } from "./Space";
import { SignupProfileForm } from "./SignupProfileForm";
import { StaleClientToast } from "./StaleClientToast";
import { ToastProvider } from "./Toast";
import { Watchtower } from "./Watchtower";
import { Workflow } from "./Workflow";
import { CHAT_WORKFLOWS_FLAG } from "../../shared/featureFlagNames";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useConnectorSealingKey } from "../hooks/useConnectorSealingKey";

export function SignedIn() {
  const z = useZero();
  const chatWorkflows = useFeatureFlag(CHAT_WORKFLOWS_FLAG);
  const [user] = useQuery(queries.currentUser());
  const [tenant] = useQuery(queries.currentTenant());
  const [preferences] = useQuery(queries.userPreferences());
  const personalWorkEnabled = usePersonalWorkEnabled();
  const sealingKey = useConnectorSealingKey();
  setSealingKey(
    sealingKey ? sealingKey.publicKey : sealingKey,
    sealingKey?.algorithm === "mlkem1024"
  );

  useColorMode({
    bindListeners: true,
    preference: (preferences?.theme as ThemePreference) ?? undefined,
    onToggle: (theme) => z.mutate(sharedMutators.setUserPreference({ theme })),
  });
  useSoundSettings(preferences);
  useBrowserNotificationSettings(preferences);
  const { open, close } = useCommandBar();
  useChordShortcuts();

  const [creditsOpen, setCreditsOpen] = useState(false);
  useKonamiCode(() => setCreditsOpen(true));

  if (
    !user ||
    sealingKey === undefined ||
    personalWorkEnabled === undefined ||
    chatWorkflows === undefined
  ) {
    return null;
  }

  if (config.signupProfileFormEnabled && !user.hasCompletedPostSignupForm) {
    if (!tenant) return null;
    return (
      <SignupProfileForm
        user={user}
        tenant={tenant}
        // Onboarding lives in Workflow, which the chat-workflows route never mounts.
        onboardingWorkflowEnabled={personalWorkEnabled && !chatWorkflows}
      />
    );
  }

  return (
    <ToastProvider>
      <StaleClientToast />
      <KeyboardShortcuts />
      {open && <CommandBar open={open} onClose={close} />}
      {creditsOpen && <Credits onClose={() => setCreditsOpen(false)} />}
      <GlobalModals />
      {Boolean(config.intercomAppId) && <Intercom user={user} />}
      <MobileNavigationProvider>
        <Switch>
          <Route path={routes.galleryImport}>
            {({ workflowId, commitId, sha256 }) => (
              <GalleryImportRoute
                workflowId={workflowId}
                commitId={commitId}
                sha256={sha256}
              />
            )}
          </Route>
          <Route path={routes.personal.workflow}>
            {({ workflowId, step, chatId, branchId }) =>
              !personalWorkEnabled ? (
                <RedirectToHome />
              ) : !chatWorkflows ? (
                <PersonalWorkflow workflowId={workflowId} step={step} />
              ) : chatId ? (
                <PersonalChats />
              ) : (
                <WorkflowChatEntry
                  spaceId=""
                  workflowId={workflowId}
                  branchId={branchId}
                  step={step}
                />
              )
            }
          </Route>
          <Route path={routes.personal}>
            {personalWorkEnabled ? <PersonalSpaceRoute /> : <RedirectToHome />}
          </Route>
          <Route path={routes.space.workflow}>
            {({ spaceId, workflowId, step, chatId, branchId }) =>
              !personalWorkEnabled || !chatWorkflows ? (
                <Workflow
                  spaceId={spaceId}
                  workflowId={workflowId}
                  step={step}
                />
              ) : chatId ? (
                <PersonalChats />
              ) : (
                <WorkflowChatEntry
                  spaceId={spaceId}
                  workflowId={workflowId}
                  branchId={branchId}
                  step={step}
                />
              )
            }
          </Route>
          <Route path={routes.space}>
            {({ spaceId }) => <Space spaceId={spaceId} />}
          </Route>
          <Route path={routes.index}>
            <RedirectToDefaultView />
          </Route>
          <Route path={routes.recents}>
            {/* New users land in their first workflow, so the intro waits for their first visit home. */}
            <NewUserIntroVideo />
            <Recents />
          </Route>
          <Route path={routes.favorites}>
            <Favorites />
          </Route>
          <Route path={routes.chats}>
            {personalWorkEnabled ? <PersonalChats /> : <RedirectToHome />}
          </Route>
          <Route path={routes.connectors}>
            <Connectors />
          </Route>
          <Route path={routes.skills}>
            <Skills />
          </Route>
          <Route path={routes.monitoring}>
            <Monitoring />
          </Route>
          <Route path={routes.links}>
            <Links />
          </Route>
          <Route path={routes.settings}>
            <Settings />
          </Route>
          <Route path={routes.watchtower}>
            <Watchtower />
          </Route>
          <Route path={routes.credits}>
            <Credits onClose={() => navigate(routes.index)} />
          </Route>
          <Route>
            <RedirectToDefaultView />
          </Route>
        </Switch>
      </MobileNavigationProvider>
    </ToastProvider>
  );
}

function Intercom({
  user,
}: {
  user: { email: string; name: string; tenantId: string };
}) {
  const { open, close, setUnreadCount, setFailed } = useIntercomMessenger();
  return (
    <IntercomMessenger
      user={user}
      open={open}
      onClose={close}
      onUnreadChange={setUnreadCount}
      onFailedChange={setFailed}
    />
  );
}

function useSoundSettings(
  preferences:
    | {
        soundClicks?: string | null;
        soundChat?: string | null;
        soundWorkflow?: string | null;
      }
    | undefined
) {
  useEffect(() => {
    soundSettings.clicks = preferences?.soundClicks !== "off";
    soundSettings.chat = preferences?.soundChat !== "off";
    soundSettings.workflow = preferences?.soundWorkflow !== "off";
  }, [
    preferences?.soundClicks,
    preferences?.soundChat,
    preferences?.soundWorkflow,
  ]);
}

function useBrowserNotificationSettings(
  preferences: { browserNotifications?: string | null } | undefined
) {
  useEffect(() => {
    browserNotificationSettings.chat =
      preferences?.browserNotifications === "on";
  }, [preferences?.browserNotifications]);
}

function PersonalSpaceRoute() {
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  if (!spaceId) return null;
  return <Space spaceId={spaceId} />;
}

function PersonalWorkflow({
  workflowId,
  step,
}: {
  workflowId: string;
  step?: string;
}) {
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  if (!spaceId) return null;
  return <Workflow spaceId={spaceId} workflowId={workflowId} step={step} />;
}

function RedirectToDefaultView() {
  const [preferences, preferencesResult] = useQuery(queries.userPreferences());
  const personalWorkEnabled = usePersonalWorkEnabled();
  const loaded =
    preferences !== undefined || preferencesResult.type === "complete";
  const destination =
    preferences?.defaultHome === "chats" && personalWorkEnabled
      ? routes.chats
      : routes.recents;
  useEffect(() => {
    if (loaded) navigate(destination, undefined, { replace: true });
  }, [loaded, destination]);
  return null;
}

function RedirectToHome() {
  useEffect(() => {
    navigate(routes.recents, undefined, { replace: true });
  }, []);
  return null;
}
